import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type {
  BoardAutomationOnCompleteDraft,
  BoardAutomationOnMoveDraft,
} from '../domain.types'
import { removeBoard } from '../shared/boardCleanup.actions'
import {
  createEmptyBoardAutomationsBoardState,
  createInitialBoardAutomationsState,
} from './boardAutomations.state.utils'
import {
  fetchBoardAutomationsThunk,
  saveBoardAutomationsThunk,
} from './boardAutomations.thunks'

const ensureBoard = (
  state: ReturnType<typeof createInitialBoardAutomationsState>,
  boardId: number,
) => {
  if (!state.byBoardId[boardId]) {
    state.byBoardId[boardId] = createEmptyBoardAutomationsBoardState()
  }
  return state.byBoardId[boardId]
}

const boardAutomationsSlice = createSlice({
  name: 'taskTrackerBoardAutomations',
  initialState: createInitialBoardAutomationsState(),
  reducers: {
    setBoardAutomationsOnCompleteDraft(
      state,
      action: PayloadAction<{
        boardId: number
        draft: BoardAutomationOnCompleteDraft
      }>,
    ) {
      ensureBoard(state, action.payload.boardId).onCompleteDraft =
        action.payload.draft
    },
    setBoardAutomationsOnMoveDraft(
      state,
      action: PayloadAction<{
        boardId: number
        draft: BoardAutomationOnMoveDraft
      }>,
    ) {
      ensureBoard(state, action.payload.boardId).onMoveDraft =
        action.payload.draft
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeBoard, (state, action) => {
        delete state.byBoardId[action.payload.boardId]
      })
      .addCase(fetchBoardAutomationsThunk.pending, (state, action) => {
        ensureBoard(state, action.meta.arg).loading = true
        ensureBoard(state, action.meta.arg).error = null
      })
      .addCase(fetchBoardAutomationsThunk.fulfilled, (state, action) => {
        const autoState = ensureBoard(state, action.payload.boardId)
        autoState.loading = false
        autoState.availableColumns = action.payload.availableColumns
        autoState.completeAutomation = action.payload.completeAutomation
        autoState.moveAutomation = action.payload.moveAutomation
        autoState.onCompleteDraft = action.payload.onCompleteDraft
        autoState.onMoveDraft = action.payload.onMoveDraft
        autoState.savedOnCompleteDraft = action.payload.savedOnCompleteDraft
        autoState.savedOnMoveDraft = action.payload.savedOnMoveDraft
      })
      .addCase(fetchBoardAutomationsThunk.rejected, (state, action) => {
        const payload = action.payload as { boardId: number; error: string }
        const autoState = ensureBoard(state, payload.boardId)
        autoState.loading = false
        autoState.error = payload.error
      })
      .addCase(saveBoardAutomationsThunk.pending, (state, action) => {
        ensureBoard(state, action.meta.arg).saving = true
        ensureBoard(state, action.meta.arg).error = null
      })
      .addCase(saveBoardAutomationsThunk.fulfilled, (state, action) => {
        const autoState = ensureBoard(state, action.meta.arg)
        autoState.saving = false
        if (!action.payload) return
        autoState.availableColumns = action.payload.availableColumns
        autoState.completeAutomation = action.payload.completeAutomation
        autoState.moveAutomation = action.payload.moveAutomation
        autoState.onCompleteDraft = action.payload.onCompleteDraft
        autoState.onMoveDraft = action.payload.onMoveDraft
        autoState.savedOnCompleteDraft = action.payload.savedOnCompleteDraft
        autoState.savedOnMoveDraft = action.payload.savedOnMoveDraft
      })
      .addCase(saveBoardAutomationsThunk.rejected, (state, action) => {
        ensureBoard(state, action.meta.arg).saving = false
      })
  },
})

export const {
  setBoardAutomationsOnCompleteDraft,
  setBoardAutomationsOnMoveDraft,
} = boardAutomationsSlice.actions

export default boardAutomationsSlice.reducer
